import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Box,
  Text,
  Input,
  Spinner,
  useToast,
  Tooltip,
  Divider,
  Badge,
  IconButton,
  Tag,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFolder,
  faFile,
  faFilePdf,
  faFileImage,
  faFileVideo,
  faFileAudio,
  faFileZipper,
  faArrowUp,
  faPlus,
  faFolderPlus,
  faTrash,
  faShare,
  faLink,
  faPencil,
  faUserPlus,
  faChevronDown,
  faUpload,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { getSupabase, supabaseSchema } from "./supabaseClient";
import { getSignedFilesUrl } from "../utils/signedUrls";
import { bucketName_files, getBackendURL, allowedFileTypes } from "./env";
import { preflightRestrictedStorageJsonSize } from "../utils/storageJsonFrontendLimit";
import {
  addGrant,
  maxRole,
  removeGrants,
  stripFolderGrantsFromFile,
  rederiveSharedWith,
  effectiveRole,
} from "../utils/driveShares";
import {
  buildInheritedFileMetadata,
  driveCapabilities,
  effectiveDriveRole,
  mergeDirectChildFileRows,
} from "../utils/driveFolderPermissions";
import { useNavigate } from "react-router-dom";
import { generateVitaMedDriveBasePath } from "../documentComponents/documentUtilities";
import ShareRowModal from "./ShareRowModal";
import Uploader from "./upload/Uploader";

// ShareRowModal speaks the app's general share vocabulary ("view"/"write"/"admin",
// see frontend/src/utils/shareAccess.js). driveShares stores grants using this
// feature's spec vocabulary ("read"/"editor"/"admin"). Translate at the boundary
// so neither side has to know about the other.
const MODAL_TO_GRANT_ROLE = {
  view: "read",
  viewer: "read",
  read: "read",
  write: "editor",
  editor: "editor",
  admin: "admin",
};
const GRANT_TO_MODAL_ROLE = {
  read: "view",
  editor: "write",
  admin: "admin",
};
const toGrantRole = (r) =>
  MODAL_TO_GRANT_ROLE[String(r || "").toLowerCase()] || "read";
const toModalRole = (r) =>
  GRANT_TO_MODAL_ROLE[String(r || "").toLowerCase()] || "view";
const mergeDriveRoles = (a, b) => {
  if (a && b) return maxRole(a, b);
  return a || b || null;
};

export default function VitaMedDriveModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const toast = useToast();

  // ── Core navigation state ──────────────────────────────────────────────────
  const [currentPath, setCurrentPath] = useState("");
  const [basePath, setBasePath] = useState("");
  const [userRootPath, setUserRootPath] = useState("");
  const [userName, setUserName] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // ── Items ──────────────────────────────────────────────────────────────────
  const [storageFolders, setStorageFolders] = useState([]);
  const [ownDocs, setOwnDocs] = useState([]);
  const [sharedDocs, setSharedDocs] = useState([]);
  const [sharedFolders, setSharedFolders] = useState([]);
  /** Storage objects at current path whose extension is in `allowedFileTypes` (not Magic Notes .json rows). */
  const [storageFiles, setStorageFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // ── Filter & sort ──────────────────────────────────────────────────────────
  const [filter, setFilter] = useState("all"); // 'all' | 'mine' | 'shared'
  const [sortOrder, setSortOrder] = useState("recent"); // 'recent' | 'oldest'

  // ── New file / folder dialogs ──────────────────────────────────────────────
  const [showNewFileDialog, setShowNewFileDialog] = useState(false);
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [newFileName, setNewFileName] = useState("Untitled");
  const [newFolderName, setNewFolderName] = useState("New Folder");
  const [isCreating, setIsCreating] = useState(false);

  // ── Share-with-users modal ─────────────────────────────────────────────────
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareTargetDoc, setShareTargetDoc] = useState(null);
  const [schemaUsers, setSchemaUsers] = useState([]);
  const [alreadySharedIds, setAlreadySharedIds] = useState(new Set());
  const [shareModalLoading, setShareModalLoading] = useState(false);
  const [shareRemovingId, setShareRemovingId] = useState(null);
  const [roleByUserId, setRoleByUserId] = useState({});

  // ── Generate-link dialog ───────────────────────────────────────────────────
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkDialogDoc, setLinkDialogDoc] = useState(null);
  const [generatedLink, setGeneratedLink] = useState("");

  // ── Oversized Magic Notes JSON (preflight) ─────────────────────────────────
  const [oversizedDriveDoc, setOversizedDriveDoc] = useState(null); // { message, doc }

  // ── Rename dialog ─────────────────────────────────────────────────────────
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null); // { type: 'file'|'folder', doc, folderName }
  const [renameValue, setRenameValue] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);

  // ── Uploaded (non-JSON) files ──────────────────────────────────────────────
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const loadItemsRequestRef = useRef(0);

  // ── Folder DB rows (keyed by path) ────────────────────────────────────────
  const [folderRowsByPath, setFolderRowsByPath] = useState({});
  const [currentFolderRow, setCurrentFolderRow] = useState(null);

  // ── Init ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || initialized) return;

    const init = async () => {
      const supabase = await getSupabase();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) return;

      setCurrentUserId(session.user.id);

      const { data: userData } = await supabase
        .from("users")
        .select("name")
        .eq("id", session.user.id)
        .single();

      const name =
        userData?.name || session.user.email?.split("@")[0] || "User";
      setUserName(name);

      const schema = supabaseSchema || "public";
      const base = generateVitaMedDriveBasePath(schema);
      setBasePath(base);

      const userRoot = `${base}${name}`;
      setUserRootPath(userRoot);
      setCurrentPath(userRoot);
      setInitialized(true);
    };

    init();
  }, [isOpen, initialized]);

  useEffect(() => {
    if (!isOpen) setInitialized(false);
  }, [isOpen]);

  // ── Folder helpers ─────────────────────────────────────────────────────────
  const ensureFolderRow = async (folderPath) => {
    const supabase = await getSupabase();
    const { data: existing } = await supabase
      .from("documents")
      .select("id, path, share_grants, shared_with")
      .eq("path", folderPath)
      .eq("is_folder", true)
      .maybeSingle();
    if (existing) return existing;
    const { data: created } = await supabase
      .from("documents")
      .insert([
        {
          path: folderPath,
          content: {},
          latest_update: new Date().toISOString(),
          client_id: "drive",
          is_folder: true,
          share_grants: [],
          shared_with: [],
        },
      ])
      .select("id, path, share_grants, shared_with")
      .single();
    return created;
  };

  const listFolderStorageItems = async (folderPath) => {
    const supabase = await getSupabase();
    const { data, error } = await supabase.storage
      .from(bucketName_files)
      .list(folderPath, { sortBy: { column: "name", order: "asc" } });
    if (error) throw error;
    return data || [];
  };

  const listFolderDocumentRows = async (folderPath) => {
    const supabase = await getSupabase();
    const { data, error } = await supabase
      .from("documents")
      .select("id, path, content, latest_update, client_id, is_folder, share_grants, shared_with")
      .like("path", `${folderPath}/%`);
    if (error) throw error;
    return data || [];
  };

  const upsertInheritedFileRows = async ({ folderPath, folderGrants }) => {
    const supabase = await getSupabase();
    const [storageItems, documentRows] = await Promise.all([
      listFolderStorageItems(folderPath),
      listFolderDocumentRows(folderPath),
    ]);

    const childRows = mergeDirectChildFileRows({
      folderPath,
      storageItems,
      documentRows,
      allowedExtensions: allowedFileTypes,
    });

    if (childRows.length === 0) return [];

    const nowIso = new Date().toISOString();
    const payload = childRows.map(({ path, documentRow }) =>
      buildInheritedFileMetadata({
        path,
        existingRow: documentRow,
        folderPath,
        folderGrants,
        nowIso,
      })
    );

    const { data, error } = await supabase
      .from("documents")
      .upsert(payload, { onConflict: "path" })
      .select("id, path, share_grants, shared_with");

    if (error) throw error;
    return data || [];
  };

  const inheritCurrentFolderGrantsForPath = async (filePath) => {
    const folderPath = filePath.substring(0, filePath.lastIndexOf("/"));
    const supabase = await getSupabase();
    const [folderResult, existingResult] = await Promise.all([
      supabase
        .from("documents")
        .select("share_grants")
        .eq("path", folderPath)
        .eq("is_folder", true)
        .maybeSingle(),
      supabase
        .from("documents")
        .select("id, path, content, latest_update, client_id, is_folder, share_grants, shared_with")
        .eq("path", filePath)
        .maybeSingle(),
    ]);

    const { data: folderRow, error } = folderResult;
    if (error) throw error;
    const { data: existingRow, error: existingError } = existingResult;
    if (existingError) throw existingError;

    const metadata = buildInheritedFileMetadata({
      path: filePath,
      existingRow,
      folderPath,
      folderGrants: folderRow?.share_grants || [],
      nowIso: new Date().toISOString(),
    });

    const { data, error: upsertError } = await supabase
      .from("documents")
      .upsert([metadata], { onConflict: "path" })
      .select("id, path, share_grants, shared_with")
      .single();
    if (upsertError) throw upsertError;
    return data;
  };

  // Recursively walk a storage prefix, deleting every file object found and
  // every matching documents row. Subfolders (storage list entries with id===null)
  // are descended into. Supabase Storage doesn't support prefix-delete; we have
  // to enumerate leaf objects.
  const deleteStorageFolderRecursive = async (folderPath) => {
    const supabase = await getSupabase();
    const { data: items } = await supabase.storage
      .from(bucketName_files)
      .list(folderPath, { sortBy: { column: "name", order: "asc" } });

    if (!items || items.length === 0) return;

    const files = items.filter((i) => i.id !== null);
    const subfolders = items.filter((i) => i.id === null);

    if (files.length > 0) {
      for (const f of files) {
        if (f.name.endsWith(".json")) {
          const fullPath = `${folderPath}/${f.name}`;
          await supabase.from("documents").delete().eq("path", fullPath);
        }
      }
      const filePaths = files.map((f) => `${folderPath}/${f.name}`);
      await supabase.storage.from(bucketName_files).remove(filePaths);
    }

    for (const sub of subfolders) {
      await deleteStorageFolderRecursive(`${folderPath}/${sub.name}`);
    }
  };

  const deleteFolderByPath = async (folderPath) => {
    const supabase = await getSupabase();
    const folderName = folderPath.split("/").pop();

    const { data: items } = await supabase.storage
      .from(bucketName_files)
      .list(folderPath, { sortBy: { column: "name", order: "asc" } });
    const visibleCount = (items || []).filter((i) => !i.name.startsWith(".")).length;

    const ok = window.confirm(
      `Delete folder "${folderName}" and the ${visibleCount} item${visibleCount === 1 ? "" : "s"} inside? This cannot be undone.`
    );
    if (!ok) return;

    try {
      await deleteStorageFolderRecursive(folderPath);

      // Drop folder row + every descendant row in documents (covers subfolder
      // rows and any files whose rows might survive a prior partial delete).
      await supabase.from("documents").delete().eq("path", folderPath);
      await supabase.from("documents").delete().like("path", `${folderPath}/%`);

      toast({
        title: "Folder deleted",
        description: `Removed "${folderName}" and ${visibleCount} item${visibleCount === 1 ? "" : "s"}.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      loadItems(currentPath);
    } catch (err) {
      console.error("deleteFolderByPath error:", err);
      toast({
        title: "Error deleting folder",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const deleteFolder = (folderName) =>
    deleteFolderByPath(`${currentPath}/${folderName}`);

  // Recipient-side delete of a folder shared to them (admin role only).
  // The folder lives outside currentPath, so delete by its absolute path.
  const deleteSharedFolderAsAdmin = (folderRow) =>
    deleteFolderByPath(folderRow.path);

  // ── Owner-vs-recipient context ─────────────────────────────────────────────
  const isOwnedPath = currentPath.startsWith(userRootPath);
  const currentFolderEffectiveRole = effectiveDriveRole(
    currentFolderRow?.share_grants,
    currentUserId
  );
  // Keep one derived capability source here while create/upload/action handlers are migrated.
  const currentFolderCapabilities = driveCapabilities({
    isOwner: isOwnedPath,
    effectiveRole: currentFolderEffectiveRole,
  });

  // ── Load items at current path ─────────────────────────────────────────────
  const loadItems = useCallback(
    async (path) => {
      if (!path) return;
      const requestId = loadItemsRequestRef.current + 1;
      loadItemsRequestRef.current = requestId;
      const isCurrentRequest = () => requestId === loadItemsRequestRef.current;
      setCurrentFolderRow(null);
      setIsLoading(true);
      try {
        const supabase = await getSupabase();

        const { data: folderRowForCurrentPath } = await supabase
          .from("documents")
          .select("id, path, share_grants, shared_with, is_folder")
          .eq("path", path)
          .eq("is_folder", true)
          .maybeSingle();
        if (!isCurrentRequest()) return;
        setCurrentFolderRow(folderRowForCurrentPath || null);

        // 1. Storage listing → subfolders
        const { data: storageItems } = await supabase.storage
          .from(bucketName_files)
          .list(path, { sortBy: { column: "name", order: "asc" } });
        if (!isCurrentRequest()) return;

        const folders = (storageItems || []).filter(
          (item) => item.id === null && !item.name.startsWith(".")
        );
        setStorageFolders(folders);

        // Fetch documents rows for each folder path so we can identify owners + read share state.
        const folderPaths = folders.map((f) => `${path}/${f.name}`);
        let nextFolderRowsByPath = {};
        if (folderPaths.length > 0) {
          const { data: folderDbRows } = await supabase
            .from("documents")
            .select("id, path, share_grants, shared_with")
            .in("path", folderPaths)
            .eq("is_folder", true);
          if (!isCurrentRequest()) return;
          nextFolderRowsByPath = Object.fromEntries(
            (folderDbRows || []).map((r) => [r.path, r])
          );
        }
        setFolderRowsByPath(nextFolderRowsByPath);

        // Non-JSON uploaded files (images, PDFs, videos, etc.)
        const nonDocFiles = (storageItems || []).filter(
          (item) => item.id !== null && !item.name.startsWith(".") && !item.name.endsWith(".json")
        );

        // 2. Own docs at this folder level (direct children only)
        const { data: allDocs } = await supabase
          .from("documents")
          .select("id, path, latest_update, shared_with, share_grants, is_folder, content, client_id")
          .like("path", `${path}/%`);
        if (!isCurrentRequest()) return;

        const metadataByPath = new Map(
          (allDocs || []).map((row) => [row.path, row])
        );
        const enrichedUploadedFiles = nonDocFiles.map((item) => {
          const fullPath = `${path}/${item.name}`;
          const docRow = metadataByPath.get(fullPath) || null;
          return {
            ...item,
            path: fullPath,
            docRow,
            effectiveRole: mergeDriveRoles(
              effectiveDriveRole(docRow?.share_grants, currentUserId),
              effectiveDriveRole(folderRowForCurrentPath?.share_grants, currentUserId)
            ),
          };
        });
        setUploadedFiles(enrichedUploadedFiles);

        const ownPathSet = new Set((allDocs || []).map((d) => d.path));

        const directDocs = (allDocs || []).filter((doc) => {
          if (doc.is_folder) return false;
          const rest = doc.path.slice(path.length + 1);
          return rest && !rest.includes("/") && rest.endsWith(".json");
        });
        setOwnDocs(directDocs);

        const fileRows = (storageItems || []).filter((item) => {
          if (item.id === null || item.name.startsWith(".")) return false;
          const ext = item.name.split(".").pop()?.toLowerCase();
          if (!ext || !allowedFileTypes.includes(ext)) return false;
          const fullPath = `${path}/${item.name}`;
          if (ownPathSet.has(fullPath)) return false;
          return true;
        });
        setStorageFiles(fileRows);

        // 3. Docs (and folders) shared with current user (only shown at root)
        if (currentUserId && path === userRootPath) {
          try {
            const { data: shared } = await supabase
              .from("documents")
              .select("id, path, latest_update, shared_with, share_grants, is_folder")
              .contains("shared_with", [currentUserId]);
            if (!isCurrentRequest()) return;
            const sharedFiles = (shared || []).filter((d) => !d.is_folder);
            const sharedFoldersList = (shared || []).filter((d) => d.is_folder);
            // Files inherited from a shared folder shouldn't appear flat in
            // recipient's root — they're reachable inside the folder. Detect
            // via the grant's source (preferred) or path-prefix fallback.
            const sharedFolderPaths = sharedFoldersList.map((f) => f.path);
            const sharedFilesAtRoot = sharedFiles.filter((f) => {
              const grants = f.share_grants || [];
              const cameFromFolder = grants.some(
                (g) => g.user_id === currentUserId && g.source === "folder"
              );
              if (cameFromFolder) return false;
              return !sharedFolderPaths.some((sp) =>
                f.path.startsWith(sp + "/")
              );
            });
            setSharedDocs(sharedFilesAtRoot);
            setSharedFolders(sharedFoldersList);
          } catch (_) {
            // shared_with / share_grants column may not exist yet
            if (!isCurrentRequest()) return;
            setSharedDocs([]);
            setSharedFolders([]);
          }
        } else if (path !== userRootPath) {
          if (!isCurrentRequest()) return;
          setSharedDocs([]);
          setSharedFolders([]);
        }
      } catch (err) {
        if (isCurrentRequest()) {
          console.error("VitaMedDrive loadItems error:", err);
        }
      } finally {
        if (isCurrentRequest()) {
          setIsLoading(false);
        }
      }
    },
    [currentUserId, userRootPath]
  );

  useEffect(() => {
    if (currentPath && isOpen) loadItems(currentPath);
  }, [currentPath, isOpen, loadItems]);

  // ── Navigation ─────────────────────────────────────────────────────────────
  const isAtBase = () => currentPath === userRootPath;

  const goUpOneLevel = () => {
    if (isAtBase()) return;
    // If we're not under our own root, we're navigating inside a folder shared
    // with us. Going up one segment would walk into the owner's territory and
    // eventually expose the whole bucket. Snap back to our own root instead.
    if (!currentPath.startsWith(userRootPath + "/") && currentPath !== userRootPath) {
      setCurrentPath(userRootPath);
      return;
    }
    const parts = currentPath.split("/").filter(Boolean);
    setCurrentPath(parts.slice(0, -1).join("/"));
  };

  const navigateInto = (folderName) =>
    setCurrentPath(`${currentPath}/${folderName}`);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getRelativePath = () => {
    if (basePath && currentPath.startsWith(basePath)) {
      return currentPath.slice(basePath.length) || "(root)";
    }
    return currentPath;
  };

  const getOwnerFromPath = (docPath) => {
    if (basePath && docPath.startsWith(basePath)) {
      return docPath.slice(basePath.length).split("/")[0];
    }
    return "Unknown";
  };

  const sanitize = (raw) =>
    raw.replace(/[/\\:*?"<>|]/g, "_").trim() || "Untitled";

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  /** Save a storage object to the user's device (private bucket; uses authenticated download). */
  const downloadDriveFile = async (storagePath, suggestedName) => {
    const name =
      suggestedName ||
      (storagePath && storagePath.split("/").pop()) ||
      "download";
    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase.storage
        .from(bucketName_files)
        .download(storagePath);
      if (error) throw error;
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({
        title: "Download started",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      console.error("downloadDriveFile:", err);
      toast({
        title: "Download failed",
        description: err?.message || String(err),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  // ── Filtered + sorted display list ────────────────────────────────────────
  // Recipient role on the doc, derived purely from grants. Null for owner
  // (an owner is never in their own share_grants); "read" / "editor" / "admin"
  // for recipients. Owner status is signalled separately by doc.isOwn.
  const decorateRole = (doc) => ({
    ...doc,
    effectiveRole: mergeDriveRoles(
      effectiveRole(doc.share_grants, currentUserId),
      doc.inheritedFolderRole
    ),
  });

  const getDisplayItems = () => {
    let docs = [];

    if (filter !== "shared") {
      // ownDocs comes from `documents WHERE path LIKE currentPath/%`. Whether
      // the current user actually OWNS those rows depends on whether currentPath
      // is under their own root — outside their root they're someone else's
      // files that they merely have read/edit access to via share_grants.
      docs = ownDocs.map((d) => ({
        ...d,
        title: d.path.split("/").pop().replace(/\.json$/, ""),
        isOwn: isOwnedPath,
        isShared: !isOwnedPath,
        sharedBy: isOwnedPath ? undefined : getOwnerFromPath(d.path),
        inheritedFolderRole: !isOwnedPath ? currentFolderEffectiveRole : null,
      })).map(decorateRole);
      // Non-JSON uploaded files are rendered separately in the uploadedFiles section
    }

    if (filter !== "mine" && currentPath === userRootPath) {
      const sharedItems = sharedDocs
        .filter((d) => !ownDocs.some((o) => o.id === d.id))
        .map((d) => ({
          ...d,
          title: d.path.split("/").pop().replace(/\.json$/, ""),
          isOwn: false,
          isShared: true,
          sharedBy: getOwnerFromPath(d.path),
        })).map(decorateRole);
      docs = [...docs, ...sharedItems];
    }

    // Deduplicate
    const seen = new Set();
    docs = docs.filter((d) => {
      if (!d.id || seen.has(d.id)) return false;
      seen.add(d.id);
      return true;
    });

    docs.sort((a, b) => {
      const at = new Date(a.latest_update || 0);
      const bt = new Date(b.latest_update || 0);
      return sortOrder === "recent" ? bt - at : at - bt;
    });

    return docs;
  };

  // ── Create new file (storage + documents table; don't navigate) ────────────
  const createNewFile = async () => {
    if (!currentFolderCapabilities.canUploadHere) {
      toast({
        title: "Upload not allowed",
        description: "You need editor or admin access to add files to this folder.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const cleanTitle = sanitize(newFileName.trim() || "Untitled");
    const filePath = `${currentPath}/${cleanTitle}.json`;
    setIsCreating(true);

    const emptyDoc = {
      sections: [],
      content: { ops: [] },
      delta: { ops: [] },
      lastUpdated: new Date().toISOString(),
    };

    try {
      let createdStorageObject = false;
      const supabase = await getSupabase();
      const blob = new Blob([JSON.stringify(emptyDoc, null, 2)], {
        type: "application/json",
      });

      const { error: uploadError } = await supabase.storage
        .from(bucketName_files)
        .upload(filePath, blob, { upsert: false, contentType: "application/json" });

      if (uploadError && !uploadError.message.includes("already exists")) {
        throw uploadError;
      }
      createdStorageObject = !uploadError;

      try {
        const [folderResult, existingResult] = await Promise.all([
          supabase
            .from("documents")
            .select("share_grants")
            .eq("path", currentPath)
            .eq("is_folder", true)
            .maybeSingle(),
          supabase
            .from("documents")
            .select("id, path, content, latest_update, client_id, is_folder, share_grants, shared_with")
            .eq("path", filePath)
            .maybeSingle(),
        ]);
        const { data: folderRow, error: folderError } = folderResult;
        if (folderError) throw folderError;
        const { data: existingRow, error: existingError } = existingResult;
        if (existingError) throw existingError;

        const nowIso = new Date().toISOString();
        const metadata = {
          ...buildInheritedFileMetadata({
            path: filePath,
            existingRow,
            folderPath: currentPath,
            folderGrants: folderRow?.share_grants || [],
            nowIso,
          }),
          content: existingRow?.content ?? { ops: [] },
        };

        const { error: upsertError } = await supabase
          .from("documents")
          .upsert([metadata], { onConflict: "path" });
        if (upsertError) throw upsertError;
      } catch (metadataError) {
        if (createdStorageObject) {
          const { error: rollbackError } = await supabase.storage
            .from(bucketName_files)
            .remove([filePath]);
          if (rollbackError) {
            console.error("Create file rollback failed:", rollbackError);
          }
        }
        throw metadataError;
      }

      setShowNewFileDialog(false);
      setNewFileName("Untitled");
      loadItems(currentPath);
      toast({
        title: "File created",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      console.error("Create file error:", err);
      toast({
        title: "Error creating file",
        description: err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCreating(false);
    }
  };

  // ── Create new folder (.keep placeholder; don't auto-navigate) ────────────
  const createNewFolder = async () => {
    if (!currentFolderCapabilities.canShare) {
      toast({
        title: "Folder creation not allowed",
        description: "You need admin access to create folders here.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const clean = sanitize(
      (newFolderName.trim() || "New Folder").replace(/\s+/g, "_")
    );
    const keepPath = `${currentPath}/${clean}/.keep`;
    setIsCreating(true);

    try {
      const supabase = await getSupabase();
      const blob = new Blob([""], { type: "text/plain" });
      await supabase.storage
        .from(bucketName_files)
        .upload(keepPath, blob, { upsert: true });

      const folderPath = `${currentPath}/${clean}`;
      await supabase
        .from("documents")
        .upsert(
          [
            {
              path: folderPath,
              content: {},
              latest_update: new Date().toISOString(),
              client_id: "drive",
              is_folder: true,
              share_grants: [],
              shared_with: [],
            },
          ],
          { onConflict: "path" }
        );

      setShowNewFolderDialog(false);
      setNewFolderName("New Folder");
      loadItems(currentPath);
      toast({
        title: "Folder created",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      console.error("Create folder error:", err);
      toast({
        title: "Error creating folder",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCreating(false);
    }
  };

  // ── Open file (Magic Notes JSON → editor; allowed uploads → signed URL) ──
  const openFile = async (doc) => {
    if (doc.isStorageFile) {
      try {
        const supabase = await getSupabase();
        const { data, error } = await supabase.storage
          .from(bucketName_files)
          .createSignedUrl(doc.path, 3600);
        if (error || !data?.signedUrl) {
          toast({
            title: "Could not open file",
            description: error?.message || "No download URL",
            status: "error",
            duration: 4000,
            isClosable: true,
          });
          return;
        }
        window.open(data.signedUrl, "_blank", "noopener,noreferrer");
      } catch (err) {
        toast({
          title: "Could not open file",
          description: err?.message || String(err),
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
      return;
    }

    const supabase = await getSupabase();
    const pf = await preflightRestrictedStorageJsonSize(
      supabase,
      bucketName_files,
      doc.path,
    );
    if (pf.status === "too_large") {
      setOversizedDriveDoc({ message: pf.message, doc });
      return;
    }
    if (pf.status === "not_found") {
      toast({
        title: "File not found",
        description: "That document is no longer in storage.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const search = doc.id ? `?docId=${doc.id}` : "";
    navigate(`/edit-document${search}`, {
      state: {
        filePath: doc.path,
        isMagicNotes: true,
        driveTitle: doc.title,
        name: doc.title,
        date_: new Date().toISOString().split("T")[0],
      },
    });
    onClose();
  };

  // ── Remove me from shared document (shared_with); does not delete file ─────
  const removeMeFromSharedDocument = async (doc) => {
    console.log("[VitaMedDrive] removeMeFromSharedDocument called", {
      docId: doc?.id,
      path: doc?.path,
      currentUserId,
      hasSharedWith: Array.isArray(doc?.shared_with),
      sharedWithLength: doc?.shared_with?.length,
    });
    if (!doc?.id || !currentUserId) {
      console.warn("[VitaMedDrive] removeMeFromSharedDocument skipped: missing doc.id or currentUserId");
      return;
    }
    try {
      const supabase = await getSupabase();
      const current = Array.isArray(doc.shared_with) ? doc.shared_with : [];
      const next = current.filter((id) => id !== currentUserId);
      console.log("[VitaMedDrive] removeMeFromSharedDocument update", {
        schema: supabaseSchema,
        docId: doc.id,
        currentLength: current.length,
        nextLength: next.length,
      });
      const { data, error } = await supabase
        .from("documents")
        .update({ shared_with: next })
        .eq("id", doc.id)
        .select();
      if (error) {
        console.error("[VitaMedDrive] removeMeFromSharedDocument error", {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
        });
        toast({
          title: "Could not remove from list",
          description: error.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        return;
      }
      console.log("[VitaMedDrive] removeMeFromSharedDocument success", { updatedRows: data?.length });
      toast({
        title: "Removed from your list",
        description: "You can be re-invited by the owner.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      loadItems(currentPath);
    } catch (err) {
      console.error("[VitaMedDrive] removeMeFromSharedDocument exception", err);
      toast({
        title: "Error",
        description: err?.message || String(err),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  // ── Delete file (own docs only: remove row + storage) ───────────────────────
  const deleteFile = async (doc) => {
    console.log("[VitaMedDrive] deleteFile called", {
      docId: doc?.id,
      path: doc?.path,
      isOwn: doc?.isOwn,
    });
    if (!doc?.path) {
      console.warn("[VitaMedDrive] deleteFile skipped: missing doc.path");
      return;
    }
    if (doc.isStorageFile) {
      try {
        const supabase = await getSupabase();
        const { error: storageError } = await supabase.storage
          .from(bucketName_files)
          .remove([doc.path]);
        if (storageError) {
          toast({
            title: "Could not delete file",
            description: storageError.message,
            status: "error",
            duration: 4000,
            isClosable: true,
          });
          return;
        }
        toast({ title: "File deleted", status: "success", duration: 2000, isClosable: true });
        loadItems(currentPath);
      } catch (err) {
        toast({
          title: "Error deleting file",
          description: err?.message || String(err),
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
      return;
    }
    try {
      const supabase = await getSupabase();
      const deleteKey = doc.id ? "id" : "path";
      const deleteValue = doc.id ?? doc.path;
      console.log("[VitaMedDrive] deleteFile documents.delete", {
        schema: supabaseSchema,
        bucket: bucketName_files,
        deleteKey,
        deleteValue,
      });
      const { data: deleteData, error: docError } = await supabase
        .from("documents")
        .delete()
        .eq(deleteKey, deleteValue)
        .select();
      if (docError) {
        console.error("[VitaMedDrive] deleteFile documents error", {
          message: docError.message,
          code: docError.code,
          details: docError.details,
          hint: docError.hint,
        });
        toast({
          title: "Could not delete document",
          description: docError.message || "Row may be protected (check RLS).",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        return;
      }
      const deletedCount = deleteData?.length ?? 0;
      console.log("[VitaMedDrive] deleteFile documents result", { deletedCount });
      if (deletedCount === 0) {
        console.warn("[VitaMedDrive] deleteFile: 0 rows deleted (RLS may be blocking). Not removing from storage.");
        toast({
          title: "Document row not deleted",
          description: "0 rows were deleted—likely an RLS policy is blocking DELETE. Run sql/policies/documents_rls_policies.sql in Supabase SQL Editor for your schema.",
          status: "error",
          duration: 6000,
          isClosable: true,
        });
        return;
      }
      console.log("[VitaMedDrive] deleteFile storage.remove", { path: doc.path });
      const { error: storageError } = await supabase.storage
        .from(bucketName_files)
        .remove([doc.path]);
      if (storageError) {
        console.error("[VitaMedDrive] deleteFile storage error", {
          message: storageError.message,
          name: storageError.name,
        });
        toast({
          title: "Document row removed but file may still be in storage",
          description: storageError.message,
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
      } else {
        console.log("[VitaMedDrive] deleteFile storage success");
      }
      toast({
        title: "File deleted",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      loadItems(currentPath);
    } catch (err) {
      console.error("[VitaMedDrive] deleteFile exception", err);
      toast({
        title: "Error deleting file",
        description: err?.message || String(err),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  // ── Rename file or folder ─────────────────────────────────────────────────
  const handleRename = async () => {
    if (!renameTarget || !renameValue.trim()) return;
    const cleanName = sanitize(renameValue.trim());
    if (!cleanName) return;
    setIsRenaming(true);
    const supabase = await getSupabase();

    try {
      if (renameTarget.type === "uploadedFile") {
        const oldName = renameTarget.item.name;
        // Preserve original extension
        const dotIdx = oldName.lastIndexOf(".");
        const ext = dotIdx !== -1 ? oldName.slice(dotIdx) : "";
        const newName = cleanName.includes(".") ? cleanName : cleanName + ext;
        const oldPath = renameTarget.item.path || `${currentPath}/${oldName}`;
        const folder = oldPath.substring(0, oldPath.lastIndexOf("/"));
        const newPath = `${folder}/${newName}`;
        if (oldPath === newPath) { setShowRenameDialog(false); return; }

        const { error: moveError } = await supabase.storage.from(bucketName_files).move(oldPath, newPath);
        if (moveError) throw moveError;

        const { error: metadataError } = await supabase
          .from("documents")
          .update({ path: newPath, latest_update: new Date().toISOString() })
          .eq("path", oldPath);
        if (metadataError) throw metadataError;
        toast({ title: "File renamed", status: "success", duration: 2000, isClosable: true });
      } else if (renameTarget.type === "file") {
        const doc = renameTarget.doc;
        const folder = doc.path.substring(0, doc.path.lastIndexOf("/"));
        const newPath = `${folder}/${cleanName}.json`;
        if (newPath === doc.path) { setShowRenameDialog(false); return; }

        await supabase.storage.from(bucketName_files).move(doc.path, newPath);
        await supabase.from("documents").update({ path: newPath }).eq("path", doc.path);

        toast({ title: "File renamed", status: "success", duration: 2000, isClosable: true });
      } else {
        // Folder rename: move all files inside
        const oldFolder = `${currentPath}/${renameTarget.folderName}`;
        const newFolder = `${currentPath}/${cleanName}`;
        if (oldFolder === newFolder) { setShowRenameDialog(false); return; }

        // List all files (recursively one level — .keep + docs)
        const { data: items } = await supabase.storage
          .from(bucketName_files)
          .list(oldFolder, { sortBy: { column: "name", order: "asc" } });

        const moves = (items || []).map((item) => {
          const oldPath = `${oldFolder}/${item.name}`;
          const newPath = `${newFolder}/${item.name}`;
          return supabase.storage.from(bucketName_files).move(oldPath, newPath).then(() => {
            // Update documents table if this was a .json file
            if (item.name.endsWith(".json")) {
              return supabase.from("documents").update({ path: newPath }).eq("path", oldPath);
            }
          });
        });
        await Promise.all(moves);

        // Rename the folder row + rewrite folder_path in any grant referencing oldFolder.
        await supabase
          .from("documents")
          .update({ path: newFolder })
          .eq("path", oldFolder)
          .eq("is_folder", true);

        const { data: affected } = await supabase
          .from("documents")
          .select("id, share_grants")
          .like("path", `${newFolder}/%`);
        await Promise.all(
          (affected || [])
            .filter((row) =>
              (row.share_grants || []).some(
                (g) => g.source === "folder" && g.folder_path === oldFolder
              )
            )
            .map((row) => {
              const next = (row.share_grants || []).map((g) =>
                g.source === "folder" && g.folder_path === oldFolder
                  ? { ...g, folder_path: newFolder }
                  : g
              );
              return supabase
                .from("documents")
                .update({ share_grants: next })
                .eq("id", row.id);
            })
        );

        toast({ title: "Folder renamed", status: "success", duration: 2000, isClosable: true });
      }

      setShowRenameDialog(false);
      setRenameTarget(null);
      setRenameValue("");
      loadItems(currentPath);
    } catch (err) {
      console.error("Rename error:", err);
      toast({ title: "Error renaming", description: err.message, status: "error", duration: 3000, isClosable: true });
    } finally {
      setIsRenaming(false);
    }
  };

  // ── Open share-with-users modal ────────────────────────────────────────────
  const openShareModal = async (doc) => {
    setShareTargetDoc(doc);
    setShareModalOpen(true);
    setShareModalLoading(true);

    try {
      const supabase = await getSupabase();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const uid = session?.user?.id;
      if (!uid) return;

      // Fetch schema users
      let users = [];
      const backendUrl = getBackendURL();
      try {
        const res = await fetch(
          `${backendUrl}/api/schema-users/?user_id=${encodeURIComponent(uid)}`
        );
        const json = await res.json().catch(() => ({}));
        if (res.ok && Array.isArray(json.users)) users = json.users;
      } catch (_) {
        const { data } = await supabase
          .from("users")
          .select("id, email, name")
          .order("email");
        users = data || [];
      }
      setSchemaUsers(users.filter((u) => u.id !== uid));

      // Fresh shared_with from DB
      const { data: docRow } = await supabase
        .from("documents")
        .select("id, shared_with, share_grants, is_folder, path")
        .eq("id", doc.id)
        .single();

      const ids = new Set(
        Array.isArray(docRow?.shared_with) ? docRow.shared_with : []
      );
      setAlreadySharedIds(ids);
      setShareTargetDoc((prev) => ({
        ...prev,
        shared_with: docRow?.shared_with,
        share_grants: docRow?.share_grants,
        is_folder: docRow?.is_folder,
        path: docRow?.path,
      }));
      // Seed roleByUserId from current grants on the target row. ShareRowModal
      // expects the modal vocabulary (view/write/admin), so translate.
      const grants = docRow?.share_grants || [];
      const seeded = {};
      for (const g of grants) {
        seeded[g.user_id] = toModalRole(g.role);
      }
      setRoleByUserId(seeded);
    } catch (err) {
      console.error("openShareModal error:", err);
    } finally {
      setShareModalLoading(false);
    }
  };

  const shareFolderWithUser = async (folderRow, user, role) => {
    const supabase = await getSupabase();
    const folderPath = folderRow.path;
    const grantRole = toGrantRole(role);
    const previousGrants = folderRow.share_grants || [];
    const previousSharedWith = rederiveSharedWith(previousGrants);

    // 1. Update the folder row.
    const folderGrants = addGrant(previousGrants, {
      user_id: user.id,
      role: grantRole,
      source: "direct",
    });
    const folderSharedWith = rederiveSharedWith(folderGrants);
    try {
      const { error: folderUpdateError } = await supabase
        .from("documents")
        .update({ share_grants: folderGrants, shared_with: folderSharedWith })
        .eq("id", folderRow.id);
      if (folderUpdateError) throw folderUpdateError;

      await upsertInheritedFileRows({
        folderPath,
        folderGrants,
      });
    } catch (err) {
      try {
        const { error: rollbackError } = await supabase
          .from("documents")
          .update({ share_grants: previousGrants, shared_with: previousSharedWith })
          .eq("id", folderRow.id);
        if (rollbackError) throw rollbackError;
      } catch (rollbackErr) {
        console.error("Folder share rollback failed:", rollbackErr);
      }
      console.error("shareFolderWithUser error:", err);
      toast({
        title: "Folder share failed",
        description: err?.message || "Could not apply folder access to every item.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setShareTargetDoc((prev) => ({
      ...prev,
      share_grants: folderGrants,
      shared_with: folderSharedWith,
    }));
    setAlreadySharedIds(new Set(folderSharedWith));
    setRoleByUserId((prev) => ({ ...prev, [user.id]: toModalRole(grantRole) }));
    toast({
      title: "Folder shared",
      description: `Shared "${folderPath.split("/").pop()}" with ${user.name || user.email} as ${toModalRole(grantRole)}`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const unshareFolderForUser = async (folderRow, user) => {
    const supabase = await getSupabase();
    const folderPath = folderRow.path;

    // 1. Strip user from folder row's grants.
    const folderGrants = removeGrants(
      folderRow.share_grants || [],
      (g) => g.user_id === user.id
    );
    const folderSharedWith = rederiveSharedWith(folderGrants);
    await supabase
      .from("documents")
      .update({ share_grants: folderGrants, shared_with: folderSharedWith })
      .eq("id", folderRow.id);

    // 2. Strip {source: folder, folder_path, user_id} from each child file.
    const { data: childRows } = await supabase
      .from("documents")
      .select("id, path, share_grants")
      .like("path", `${folderPath}/%`);

    const directChildren = (childRows || []).filter((d) => {
      const rest = d.path.slice(folderPath.length + 1);
      return rest && !rest.includes("/");
    });

    await Promise.all(
      directChildren.map((child) => {
        const nextGrants = stripFolderGrantsFromFile(
          child.share_grants || [],
          folderPath,
          user.id
        );
        return supabase
          .from("documents")
          .update({
            share_grants: nextGrants,
            shared_with: rederiveSharedWith(nextGrants),
          })
          .eq("id", child.id);
      })
    );

    setShareTargetDoc((prev) => ({
      ...prev,
      share_grants: folderGrants,
      shared_with: folderSharedWith,
    }));
    setAlreadySharedIds(new Set(folderSharedWith));
    setRoleByUserId((prev) => {
      const next = { ...prev };
      delete next[user.id];
      return next;
    });
    toast({ title: "Access removed", status: "success", duration: 2000, isClosable: true });
  };

  // Batch-share with many users in a single DB update. Avoids the stale-state
  // problem of looping handleShare (each call reads shareTargetDoc.share_grants
  // from a closure that doesn't update between awaits, so the last write wins).
  const handleShareMany = async (entries) => {
    if (!shareTargetDoc?.id || !entries || entries.length === 0) return;
    const supabase = await getSupabase();

    if (shareTargetDoc.is_folder) {
      const folderPath = shareTargetDoc.path;
      const previousGrants = shareTargetDoc.share_grants || [];
      const previousSharedWith = rederiveSharedWith(previousGrants);
      let folderGrants = previousGrants;
      const nextRoleByUserId = { ...roleByUserId };

      for (const e of entries) {
        const grantRole = toGrantRole(e.role);
        folderGrants = addGrant(folderGrants, {
          user_id: e.user.id,
          role: grantRole,
          source: "direct",
        });
        nextRoleByUserId[e.user.id] = toModalRole(grantRole);
      }
      const folderSharedWith = rederiveSharedWith(folderGrants);
      try {
        const { error: folderUpdateError } = await supabase
          .from("documents")
          .update({ share_grants: folderGrants, shared_with: folderSharedWith })
          .eq("id", shareTargetDoc.id);
        if (folderUpdateError) throw folderUpdateError;

        await upsertInheritedFileRows({
          folderPath,
          folderGrants,
        });
      } catch (err) {
        try {
          const { error: rollbackError } = await supabase
            .from("documents")
            .update({ share_grants: previousGrants, shared_with: previousSharedWith })
            .eq("id", shareTargetDoc.id);
          if (rollbackError) throw rollbackError;
        } catch (rollbackErr) {
          console.error("Folder batch-share rollback failed:", rollbackErr);
        }
        console.error("handleShareMany folder error:", err);
        toast({
          title: "Folder share failed",
          description: err?.message || "Could not apply folder access to every item.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      setShareTargetDoc((prev) => ({
        ...prev,
        share_grants: folderGrants,
        shared_with: folderSharedWith,
      }));
      setAlreadySharedIds(new Set(folderSharedWith));
      setRoleByUserId(nextRoleByUserId);
      toast({
        title: "Folder shared",
        description: `Shared "${folderPath.split("/").pop()}" with ${entries.length} user${entries.length === 1 ? "" : "s"}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    let nextGrants = shareTargetDoc.share_grants || [];
    const nextRoleByUserId = { ...roleByUserId };
    for (const e of entries) {
      const grantRole = toGrantRole(e.role);
      nextGrants = addGrant(nextGrants, {
        user_id: e.user.id,
        role: grantRole,
        source: "direct",
      });
      nextRoleByUserId[e.user.id] = toModalRole(grantRole);
    }
    const nextSharedWith = rederiveSharedWith(nextGrants);
    await supabase
      .from("documents")
      .update({ share_grants: nextGrants, shared_with: nextSharedWith })
      .eq("id", shareTargetDoc.id);

    setShareTargetDoc((prev) => ({
      ...prev,
      share_grants: nextGrants,
      shared_with: nextSharedWith,
    }));
    setAlreadySharedIds(new Set(nextSharedWith));
    setRoleByUserId(nextRoleByUserId);
    toast({
      title: "Shared",
      description: `Shared with ${entries.length} user${entries.length === 1 ? "" : "s"}`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleShare = async (user, role) => {
    if (!shareTargetDoc?.id) return;
    const supabase = await getSupabase();
    const grantRole = toGrantRole(role || roleByUserId[user.id]);

    if (shareTargetDoc.is_folder) {
      return shareFolderWithUser(shareTargetDoc, user, grantRole);
    }

    const currentGrants = shareTargetDoc.share_grants || [];
    const nextGrants = addGrant(currentGrants, {
      user_id: user.id,
      role: grantRole,
      source: "direct",
    });
    const nextSharedWith = rederiveSharedWith(nextGrants);

    await supabase
      .from("documents")
      .update({ share_grants: nextGrants, shared_with: nextSharedWith })
      .eq("id", shareTargetDoc.id);

    setShareTargetDoc((prev) => ({
      ...prev,
      share_grants: nextGrants,
      shared_with: nextSharedWith,
    }));
    setAlreadySharedIds(new Set(nextSharedWith));
    setRoleByUserId((prev) => ({ ...prev, [user.id]: toModalRole(grantRole) }));
    toast({
      title: "Shared",
      description: `Shared with ${user.name || user.email} as ${toModalRole(grantRole)}`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleRemoveShare = async (user) => {
    if (!shareTargetDoc?.id) return;
    setShareRemovingId(user.id);
    const supabase = await getSupabase();

    if (shareTargetDoc.is_folder) {
      await unshareFolderForUser(shareTargetDoc, user);
      setShareRemovingId(null);
      return;
    }

    const currentGrants = shareTargetDoc.share_grants || [];
    const nextGrants = removeGrants(
      currentGrants,
      (g) => g.user_id === user.id && g.source === "direct"
    );
    const nextSharedWith = rederiveSharedWith(nextGrants);

    await supabase
      .from("documents")
      .update({ share_grants: nextGrants, shared_with: nextSharedWith })
      .eq("id", shareTargetDoc.id);

    setShareTargetDoc((prev) => ({
      ...prev,
      share_grants: nextGrants,
      shared_with: nextSharedWith,
    }));
    setAlreadySharedIds(new Set(nextSharedWith));
    setRoleByUserId((prev) => {
      const next = { ...prev };
      delete next[user.id];
      return next;
    });
    setShareRemovingId(null);
    toast({
      title: "Access removed",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  // ── Generate shareable link (uses documents table row id) ─────────────────
  const generateLink = (doc) => {
    if (!doc.id) {
      toast({
        title: "No document ID found",
        description: "Save the document first.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    // Build absolute URL relative to current page location
    const base =
      window.location.origin +
      window.location.pathname.replace(/[^/]*$/, "");
    const link = `${base}edit-document?docId=${doc.id}`;
    setGeneratedLink(link);
    setLinkDialogDoc(doc);
    setShowLinkDialog(true);
  };

  // ── File upload (any type) ────────────────────────────────────────────────
  const handleFileUpload = async (file) => {
    if (!file || !currentPath) return;
    if (!currentFolderCapabilities.canUploadHere) {
      toast({
        title: "Upload not allowed",
        description: "You need editor or admin access to add files to this folder.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsUploading(true);
    try {
      const supabase = await getSupabase();
      const safeName = file.name.replace(/[/\\:*?"<>|]/g, "_");
      const uploadPath = `${currentPath}/${safeName}`;
      const { data: existingItems, error: existingListError } = await supabase.storage
        .from(bucketName_files)
        .list(currentPath, { sortBy: { column: "name", order: "asc" } });
      if (existingListError) throw existingListError;
      const didReplaceExisting = (existingItems || []).some(
        (item) => item.id !== null && item.name === safeName
      );

      const { error } = await supabase.storage
        .from(bucketName_files)
        .upload(uploadPath, file, { upsert: true, contentType: file.type || "application/octet-stream" });
      if (error) throw error;
      try {
        await inheritCurrentFolderGrantsForPath(uploadPath);
      } catch (metadataError) {
        if (didReplaceExisting) {
          console.error("Upload metadata update failed after overwrite:", metadataError);
          toast({
            title: "File replaced, metadata update failed",
            description: "The file was replaced, but sharing metadata could not be updated. The folder was reloaded.",
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
          loadItems(currentPath);
          return;
        }

        const { error: rollbackError } = await supabase.storage
          .from(bucketName_files)
          .remove([uploadPath]);
        if (rollbackError) {
          console.error("Upload rollback failed:", rollbackError);
        }
        throw metadataError;
      }
      toast({ title: "File uploaded", description: safeName, status: "success", duration: 2000, isClosable: true });
      loadItems(currentPath);
    } catch (err) {
      toast({ title: "Upload failed", description: err.message, status: "error", duration: 3000, isClosable: true });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const getPublicFileUrl = async (storagePath) => {
    // Signed URL so opening files keeps working when `files` is a private bucket.
    return getSignedFilesUrl(storagePath);
  };

  const openUploadedFile = async (item) => {
    const path = item.path || `${currentPath}/${item.name}`;
    const url = await getPublicFileUrl(path);
    if (url) window.open(url, "_blank");
  };

  const deleteUploadedFile = async (item) => {
    try {
      const supabase = await getSupabase();
      const path = item.path || `${currentPath}/${item.name}`;
      // Also drop any documents stub row for this path. openShareModalForUploadedFile
      // creates one when an uploaded file is shared; without this delete, recipients
      // keep seeing a ghost entry via .contains("shared_with", ...) after the storage
      // file is gone.
      await supabase.from("documents").delete().eq("path", path);
      await supabase.storage.from(bucketName_files).remove([path]);
      toast({ title: "File deleted", status: "success", duration: 2000, isClosable: true });
      loadItems(currentPath);
    } catch (err) {
      toast({ title: "Delete failed", description: err.message, status: "error", duration: 3000, isClosable: true });
    }
  };

  const openShareModalForUploadedFile = async (item) => {
    const storagePath = item.path || `${currentPath}/${item.name}`;
    try {
      const supabase = await getSupabase();

      // Look for an existing documents stub for this storage file
      let { data: existingDoc } = await supabase
        .from("documents")
        .select("id, path, shared_with, share_grants")
        .eq("path", storagePath)
        .maybeSingle();

      if (!existingDoc) {
        // Create a lightweight stub so we can persist shared_with
        const { data: newDoc, error } = await supabase
          .from("documents")
          .insert({
            path: storagePath,
            content: {},
            latest_update: new Date().toISOString(),
            client_id: "drive",
            is_folder: false,
            share_grants: [],
            shared_with: [],
          })
          .select("id, path, shared_with, share_grants")
          .single();
        if (error) throw error;
        existingDoc = newDoc;
      }

      openShareModal({ id: existingDoc.id, title: item.name });
    } catch (err) {
      toast({ title: "Error preparing share", description: err.message, status: "error", duration: 3000, isClosable: true });
    }
  };

  const generateUploadedFileLink = async (item) => {
    const path = item.path || `${currentPath}/${item.name}`;
    const url = await getPublicFileUrl(path);
    if (!url) {
      toast({ title: "Could not generate link", status: "error", duration: 3000, isClosable: true });
      return;
    }
    setGeneratedLink(url);
    setLinkDialogDoc({ title: item.name });
    setShowLinkDialog(true);
  };

  const getFileIcon = (name = "") => {
    const ext = name.split(".").pop().toLowerCase();
    if (["pdf"].includes(ext)) return faFilePdf;
    if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg", "heic"].includes(ext)) return faFileImage;
    if (["mp4", "mov", "avi", "mkv", "webm", "m4v"].includes(ext)) return faFileVideo;
    if (["mp3", "wav", "ogg", "m4a", "aac", "webm"].includes(ext)) return faFileAudio;
    if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) return faFileZipper;
    return faFile;
  };

  const getFileIconColor = (name = "") => {
    const ext = name.split(".").pop().toLowerCase();
    if (["pdf"].includes(ext)) return "red.500";
    if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg", "heic"].includes(ext)) return "green.500";
    if (["mp4", "mov", "avi", "mkv", "webm", "m4v"].includes(ext)) return "purple.500";
    if (["mp3", "wav", "ogg", "m4a", "aac"].includes(ext)) return "orange.500";
    return "gray.500";
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const displayItems = getDisplayItems();

  return (
    <>
      <Modal
        isOpen={!!oversizedDriveDoc}
        onClose={() => setOversizedDriveDoc(null)}
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>File too large to open</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={2}>
              This Magic Notes file was not loaded because it exceeds the size
              limit. Open the editor with a blank document instead? Saving will
              overwrite the large file when you save.
            </Text>
            <Text fontSize="sm" color="gray.600">
              {oversizedDriveDoc?.message}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => setOversizedDriveDoc(null)}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => {
                const doc = oversizedDriveDoc?.doc;
                setOversizedDriveDoc(null);
                if (!doc) return;
                const search = doc.id ? `?docId=${doc.id}` : "";
                navigate(`/edit-document${search}`, {
                  state: {
                    filePath: doc.path,
                    isMagicNotes: true,
                    driveTitle: doc.title,
                    name: doc.title,
                    date_: new Date().toISOString().split("T")[0],
                    createNew: true,
                    skipLargeStorageJsonFetch: true,
                  },
                });
                onClose();
              }}
            >
              Create new
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxW="820px" maxH="88vh">
          <ModalHeader borderBottomWidth="1px" pb={3}>
            <HStack spacing={3} flexWrap="wrap">
              <Text fontSize="lg" fontWeight="bold">
                Vita-Med Drive
              </Text>
              <Badge
                colorScheme="blue"
                fontSize="xs"
                maxW="380px"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {getRelativePath()}
              </Badge>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody py={4}>
            {/* ── Toolbar ─────────────────────────────────────────────── */}
            <HStack mb={3} spacing={2} flexWrap="wrap">
              <Button
                size="sm"
                leftIcon={<FontAwesomeIcon icon={faArrowUp} />}
                onClick={goUpOneLevel}
                isDisabled={isAtBase()}
                variant="outline"
                colorScheme="gray"
              >
                Up One Level
              </Button>
              {currentFolderCapabilities.canUploadHere && (
                <Button
                  size="sm"
                  colorScheme="blue"
                  leftIcon={<FontAwesomeIcon icon={faPlus} />}
                  onClick={() => {
                    setNewFileName("Untitled");
                    setShowNewFileDialog(true);
                    setShowNewFolderDialog(false);
                  }}
                >
                  New File
                </Button>
              )}
              {currentFolderCapabilities.canShare && (
                <Button
                  size="sm"
                  colorScheme="teal"
                  leftIcon={<FontAwesomeIcon icon={faFolderPlus} />}
                  onClick={() => {
                    setNewFolderName("New Folder");
                    setShowNewFolderDialog(true);
                    setShowNewFileDialog(false);
                  }}
                >
                  New Folder
                </Button>
              )}
              {currentFolderCapabilities.canUploadHere && (
                <Button
                  size="sm"
                  colorScheme="orange"
                  leftIcon={<FontAwesomeIcon icon={faUpload} />}
                  isLoading={isUploading}
                  loadingText="Uploading..."
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload File
                </Button>
              )}
              {!isAtBase() && currentFolderCapabilities.canShare && (
                <Button
                  size="sm"
                  colorScheme="green"
                  leftIcon={<FontAwesomeIcon icon={faUserPlus} />}
                  onClick={async () => {
                    const row = currentFolderRow || (await ensureFolderRow(currentPath));
                    openShareModal({
                      ...row,
                      title: currentPath.split("/").pop(),
                      is_folder: true,
                    });
                  }}
                >
                  Share Folder
                </Button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="*/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileUpload(file);
                }}
              />

              {/* Filter + Sort — pushed to right */}
              <HStack spacing={1} ml="auto">
                {[
                  { key: "all", label: "All" },
                  { key: "mine", label: "My Files" },
                  { key: "shared", label: "Shared" },
                ].map(({ key, label }) => (
                  <Button
                    key={key}
                    size="xs"
                    variant={filter === key ? "solid" : "outline"}
                    colorScheme="gray"
                    onClick={() => setFilter(key)}
                  >
                    {label}
                  </Button>
                ))}
                <Box position="relative" w="130px" ml={1} h="24px" flexShrink={0}>
                  <Box
                    as="select"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    w="100%"
                    h="100%"
                    pr="24px"
                    pl={2}
                    fontSize="xs"
                    lineHeight="24px"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="gray.200"
                    bg="white"
                    cursor="pointer"
                    appearance="none"
                    _hover={{ borderColor: "gray.300" }}
                    _focus={{ outline: "none", borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                  >
                    <option value="recent">Most Recent</option>
                    <option value="oldest">Least Recent</option>
                  </Box>
                  <Box
                    position="absolute"
                    right="6px"
                    top="50%"
                    transform="translateY(-50%)"
                    pointerEvents="none"
                    color="gray.500"
                    fontSize="10px"
                  >
                    <FontAwesomeIcon icon={faChevronDown} />
                  </Box>
                </Box>
              </HStack>
            </HStack>

            {/* ── New File Dialog ──────────────────────────────────────── */}
            {showNewFileDialog && (
              <Box
                mb={3}
                p={3}
                border="1px solid"
                borderColor="blue.300"
                borderRadius="md"
                bg="blue.50"
              >
                <Text
                  fontWeight="semibold"
                  mb={2}
                  fontSize="sm"
                  color="blue.700"
                >
                  New Magic Notes File
                </Text>
                <HStack>
                  <Input
                    value={newFileName}
                    onChange={(e) => setNewFileName(e.target.value)}
                    size="sm"
                    bg="white"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") createNewFile();
                      if (e.key === "Escape") setShowNewFileDialog(false);
                    }}
                  />
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={createNewFile}
                    isLoading={isCreating}
                  >
                    Create
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowNewFileDialog(false)}
                  >
                    Cancel
                  </Button>
                </HStack>
              </Box>
            )}

            {/* ── New Folder Dialog ────────────────────────────────────── */}
            {showNewFolderDialog && (
              <Box
                mb={3}
                p={3}
                border="1px solid"
                borderColor="teal.300"
                borderRadius="md"
                bg="teal.50"
              >
                <Text
                  fontWeight="semibold"
                  mb={2}
                  fontSize="sm"
                  color="teal.700"
                >
                  New Folder
                </Text>
                <HStack>
                  <Input
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    size="sm"
                    bg="white"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") createNewFolder();
                      if (e.key === "Escape") setShowNewFolderDialog(false);
                    }}
                  />
                  <Button
                    size="sm"
                    colorScheme="teal"
                    onClick={createNewFolder}
                    isLoading={isCreating}
                  >
                    Create
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowNewFolderDialog(false)}
                  >
                    Cancel
                  </Button>
                </HStack>
              </Box>
            )}

            {/* ── Share-link Dialog ────────────────────────────────────── */}
            {showLinkDialog && linkDialogDoc && (
              <Box
                mb={3}
                p={3}
                border="1px solid"
                borderColor="purple.300"
                borderRadius="md"
                bg="purple.50"
              >
                <Text
                  fontWeight="semibold"
                  mb={1}
                  fontSize="sm"
                  color="purple.700"
                >
                  Share Link — {linkDialogDoc.title}
                </Text>
                <Text fontSize="xs" color="gray.500" mb={2}>
                  Anyone who opens this link will be added as a collaborator.
                </Text>
                <HStack>
                  <Input value={generatedLink} isReadOnly size="sm" bg="white" />
                  <Tooltip label="Copy link">
                    <Button
                      size="sm"
                      colorScheme="purple"
                      leftIcon={<FontAwesomeIcon icon={faLink} />}
                      onClick={() => copyToClipboard(generatedLink)}
                    >
                      Copy
                    </Button>
                  </Tooltip>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowLinkDialog(false)}
                  >
                    Close
                  </Button>
                </HStack>
              </Box>
            )}

            <Divider mb={3} />

            {/* ── Listing ──────────────────────────────────────────────── */}
            {isLoading ? (
              <VStack py={8} spacing={2}>
                <Spinner />
                <Text fontSize="sm" color="gray.500">
                  Loading…
                </Text>
              </VStack>
            ) : storageFolders.length === 0 && displayItems.length === 0 && sharedFolders.length === 0 && uploadedFiles.length === 0 ? (
              <VStack py={10} color="gray.400">
                <Text fontSize="sm">This folder is empty.</Text>
                <Text fontSize="xs">
                  Use New File, New Folder, or Upload ({allowedFileTypes.join(", ")}).
                </Text>
              </VStack>
            ) : (
              <VStack spacing={0} align="stretch">
                {/* Folders */}
                {storageFolders.map((folder) => (
                  <HStack
                    key={folder.name}
                    px={3}
                    py={2}
                    borderRadius="md"
                    _hover={{ bg: "gray.50" }}
                    cursor="pointer"
                    onClick={() => navigateInto(folder.name)}
                    borderBottomWidth="1px"
                    borderColor="gray.100"
                  >
                    <Box color="yellow.500" w={5} textAlign="center">
                      <FontAwesomeIcon icon={faFolder} />
                    </Box>
                    <Text
                      flex={1}
                      fontSize="sm"
                      fontWeight="semibold"
                      isTruncated
                    >
                      {folder.name}
                    </Text>
                    <HStack spacing={1} onClick={(e) => e.stopPropagation()}>
                      {(() => {
                        const folderPath = `${currentPath}/${folder.name}`;
                        const folderRow = folderRowsByPath[folderPath];
                        const folderRole = effectiveDriveRole(folderRow?.share_grants, currentUserId);
                        const folderCaps = driveCapabilities({
                          isOwner: isOwnedPath,
                          effectiveRole: folderRole,
                        });
                        return (
                          <>
                            <Text fontSize="xs" color="gray.400">Folder ›</Text>
                            {folderCaps.canShare && (
                              <Tooltip label="Share folder">
                                <IconButton
                                  size="xs"
                                  icon={<FontAwesomeIcon icon={faUserPlus} />}
                                  colorScheme="gray"
                                  variant="ghost"
                                  aria-label="Share folder"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const existing = folderRowsByPath[folderPath];
                                    const row = existing || (await ensureFolderRow(folderPath));
                                    openShareModal({
                                      ...row,
                                      title: folder.name,
                                      is_folder: true,
                                    });
                                  }}
                                />
                              </Tooltip>
                            )}
                            {folderCaps.canRename && (
                              <Tooltip label="Rename folder">
                                <IconButton
                                  size="xs"
                                  icon={<FontAwesomeIcon icon={faPencil} />}
                                  colorScheme="gray"
                                  variant="ghost"
                                  aria-label="Rename folder"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setRenameTarget({ type: "folder", folderName: folder.name });
                                    setRenameValue(folder.name);
                                    setShowRenameDialog(true);
                                  }}
                                />
                              </Tooltip>
                            )}
                            {folderCaps.canDelete && (
                              <Tooltip label="Delete folder">
                                <IconButton
                                  size="xs"
                                  icon={<FontAwesomeIcon icon={faTrash} />}
                                  colorScheme="red"
                                  variant="ghost"
                                  aria-label="Delete folder"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteFolder(folder.name);
                                  }}
                                />
                              </Tooltip>
                            )}
                          </>
                        );
                      })()}
                    </HStack>
                  </HStack>
                ))}

                {/* Shared folders (recipient root view) */}
                {sharedFolders.map((folderRow) => {
                  const name = folderRow.path.split("/").pop();
                  const role = effectiveDriveRole(folderRow.share_grants, currentUserId) || "read";
                  const folderCaps = driveCapabilities({
                    isOwner: false,
                    effectiveRole: role,
                  });
                  const owner = getOwnerFromPath(folderRow.path);
                  return (
                    <HStack
                      key={`shared-folder-${folderRow.id}`}
                      px={3}
                      py={2}
                      borderRadius="md"
                      bg="purple.50"
                      _hover={{ bg: "purple.100" }}
                      cursor="pointer"
                      onClick={() => setCurrentPath(folderRow.path)}
                      borderBottomWidth="1px"
                      borderColor="purple.100"
                    >
                      <Box color="purple.500" w={5} textAlign="center">
                        <FontAwesomeIcon icon={faFolder} />
                      </Box>
                      <VStack align="start" flex={1} spacing={0} minW={0}>
                        <Text fontSize="sm" fontWeight="semibold" isTruncated maxW="100%">
                          {name}
                        </Text>
                        <Text fontSize="xs" color="purple.600" fontStyle="italic">
                          Shared from: {owner}
                        </Text>
                      </VStack>
                      <HStack spacing={1} onClick={(e) => e.stopPropagation()}>
                        <Tag size="sm" colorScheme="purple" variant="subtle">{role}</Tag>
                        {folderCaps.canShare && (
                          <Tooltip label="Share folder">
                            <IconButton
                              size="xs"
                              icon={<FontAwesomeIcon icon={faUserPlus} />}
                              colorScheme="green"
                              variant="ghost"
                              aria-label="Share folder"
                              onClick={(e) => {
                                e.stopPropagation();
                                openShareModal({
                                  ...folderRow,
                                  title: name,
                                  is_folder: true,
                                });
                              }}
                            />
                          </Tooltip>
                        )}
                        {folderCaps.canDelete && (
                          <Tooltip label="Delete folder">
                            <IconButton
                              size="xs"
                              icon={<FontAwesomeIcon icon={faTrash} />}
                              colorScheme="red"
                              variant="ghost"
                              aria-label="Delete folder"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Reuse the cascade by jumping into the folder's parent
                                // path-relative name; deleteFolder needs currentPath set
                                // to the parent. For shared folders shown at root, we
                                // delete via the folder row's full path directly.
                                deleteSharedFolderAsAdmin(folderRow);
                              }}
                            />
                          </Tooltip>
                        )}
                        <Text fontSize="xs" color="purple.500">Shared ›</Text>
                      </HStack>
                    </HStack>
                  );
                })}

                {/* Files */}
                {displayItems.map((doc) => {
                  const docCaps = driveCapabilities({
                    isOwner: doc.isOwn,
                    effectiveRole: doc.effectiveRole,
                  });

                  return (
                    <HStack
                      key={doc.id || doc.path}
                      px={3}
                      py={2}
                      borderRadius="md"
                      bg={doc.isShared ? "purple.50" : undefined}
                      borderBottomWidth="1px"
                      borderColor={doc.isShared ? "purple.100" : "gray.100"}
                      align="center"
                      cursor="pointer"
                      _hover={{ bg: doc.isShared ? "purple.100" : "gray.50" }}
                      onClick={() => openFile(doc)}
                    >
                    <Box
                      color={doc.isShared ? "purple.400" : "blue.400"}
                      w={5}
                      textAlign="center"
                      flexShrink={0}
                    >
                      <FontAwesomeIcon icon={faFile} />
                    </Box>

                    <VStack align="start" flex={1} spacing={0} minW={0}>
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        isTruncated
                        maxW="100%"
                      >
                        {doc.title}
                      </Text>
                      {doc.isShared && (
                        <Text fontSize="xs" color="purple.600" fontStyle="italic">
                          Shared from: {doc.sharedBy}
                        </Text>
                      )}
                      {doc.latest_update && (
                        <Text fontSize="xs" color="gray.400">
                          {new Date(doc.latest_update).toLocaleString()}
                        </Text>
                      )}
                    </VStack>

                    <HStack
                      spacing={1}
                      flexShrink={0}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        size="xs"
                        colorScheme="blue"
                        onClick={(e) => {
                          e.stopPropagation();
                          openFile(doc);
                        }}
                      >
                        Open
                      </Button>
                      <Tooltip label="Download">
                        <IconButton
                          size="xs"
                          icon={<FontAwesomeIcon icon={faDownload} />}
                          colorScheme="gray"
                          variant="outline"
                          aria-label="Download file"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadDriveFile(
                              doc.path,
                              doc.path.split("/").pop()
                            );
                          }}
                        />
                      </Tooltip>
                      {docCaps.canRename && !doc.isStorageFile && (
                        <>
                          <Tooltip label="Rename file">
                            <IconButton
                              size="xs"
                              icon={<FontAwesomeIcon icon={faPencil} />}
                              colorScheme="gray"
                              aria-label="Rename file"
                              onClick={() => {
                                setRenameTarget({ type: "file", doc });
                                setRenameValue(doc.title);
                                setShowRenameDialog(true);
                              }}
                            />
                          </Tooltip>
                        </>
                      )}
                      {docCaps.canShare && !doc.isStorageFile && (
                        <>
                          <Tooltip label="Share with users">
                            <IconButton
                              size="xs"
                              icon={<FontAwesomeIcon icon={faShare} />}
                              colorScheme="green"
                              aria-label="Share with users"
                              onClick={() => openShareModal(doc)}
                            />
                          </Tooltip>
                        </>
                      )}
                      {docCaps.canGenerateLink && !doc.isStorageFile && (
                        <>
                          <Tooltip label="Generate share link">
                            <IconButton
                              size="xs"
                              icon={<FontAwesomeIcon icon={faLink} />}
                              colorScheme="purple"
                              aria-label="Generate link"
                              onClick={() => generateLink(doc)}
                            />
                          </Tooltip>
                        </>
                      )}
                      {docCaps.canDelete && (
                        <Tooltip label="Delete file">
                          <IconButton
                            size="xs"
                            icon={<FontAwesomeIcon icon={faTrash} />}
                            colorScheme="red"
                            aria-label="Delete"
                            onClick={() => {
                              if (window.confirm(`Delete "${doc.title}"?`))
                                deleteFile(doc);
                            }}
                          />
                        </Tooltip>
                      )}
                      {doc.isShared && (
                        <Tooltip label="Remove from my list (stops sharing with you)">
                          <IconButton
                            size="xs"
                            icon={<FontAwesomeIcon icon={faTrash} />}
                            colorScheme="red"
                            variant="outline"
                            aria-label="Remove from my list"
                            onClick={() => {
                              if (window.confirm(`Remove "${doc.title}" from your list? You can be re-invited by the owner.`))
                                removeMeFromSharedDocument(doc);
                            }}
                          />
                        </Tooltip>
                      )}
                    </HStack>
                    </HStack>
                  );
                })}

                {/* Uploaded files (non-doc) */}
                {uploadedFiles.map((item) => {
                  const itemCaps = driveCapabilities({
                    isOwner: isOwnedPath,
                    effectiveRole: item.effectiveRole,
                  });

                  return (
                    <HStack
                      key={item.name}
                      px={3}
                      py={2}
                      borderRadius="md"
                      bg={!isOwnedPath ? "purple.50" : undefined}
                      borderBottomWidth="1px"
                      borderColor={!isOwnedPath ? "purple.100" : "gray.100"}
                      align="center"
                      cursor="pointer"
                      _hover={{ bg: !isOwnedPath ? "purple.100" : "gray.50" }}
                      onClick={() => openUploadedFile(item)}
                    >
                    <Box
                      color={!isOwnedPath ? "purple.400" : getFileIconColor(item.name)}
                      w={5}
                      textAlign="center"
                      flexShrink={0}
                    >
                      <FontAwesomeIcon icon={getFileIcon(item.name)} />
                    </Box>

                    <VStack align="start" flex={1} spacing={0} minW={0}>
                      <Text fontSize="sm" fontWeight="medium" isTruncated maxW="100%">
                        {item.name}
                      </Text>
                      {!isOwnedPath && (
                        <Text fontSize="xs" color="purple.600" fontStyle="italic">
                          Shared from: {getOwnerFromPath(`${currentPath}/${item.name}`)}
                        </Text>
                      )}
                      {item.updated_at && (
                        <Text fontSize="xs" color="gray.400">
                          {new Date(item.updated_at).toLocaleString()}
                        </Text>
                      )}
                    </VStack>

                    <HStack
                      spacing={1}
                      flexShrink={0}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        size="xs"
                        colorScheme="blue"
                        onClick={() => openUploadedFile(item)}
                      >
                        Open
                      </Button>
                      <Tooltip label="Download">
                        <IconButton
                          size="xs"
                          icon={<FontAwesomeIcon icon={faDownload} />}
                          colorScheme="gray"
                          variant="outline"
                          aria-label="Download file"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadDriveFile(
                              `${currentPath}/${item.name}`,
                              item.name
                            );
                          }}
                        />
                      </Tooltip>
                      {itemCaps.canRename && (
                        <Tooltip label="Rename file">
                          <IconButton
                            size="xs"
                            icon={<FontAwesomeIcon icon={faPencil} />}
                            colorScheme="gray"
                            aria-label="Rename file"
                            onClick={() => {
                              const dotIdx = item.name.lastIndexOf(".");
                              setRenameTarget({ type: "uploadedFile", item });
                              setRenameValue(dotIdx !== -1 ? item.name.slice(0, dotIdx) : item.name);
                              setShowRenameDialog(true);
                            }}
                          />
                        </Tooltip>
                      )}
                      {itemCaps.canShare && (
                        <Tooltip label="Share with users">
                          <IconButton
                            size="xs"
                            icon={<FontAwesomeIcon icon={faShare} />}
                            colorScheme="green"
                            aria-label="Share with users"
                            onClick={() => openShareModalForUploadedFile(item)}
                          />
                        </Tooltip>
                      )}
                      {itemCaps.canGenerateLink && (
                        <Tooltip label="Generate share link">
                          <IconButton
                            size="xs"
                            icon={<FontAwesomeIcon icon={faLink} />}
                            colorScheme="purple"
                            aria-label="Generate link"
                            onClick={() => generateUploadedFileLink(item)}
                          />
                        </Tooltip>
                      )}
                      {itemCaps.canDelete && (
                        <Tooltip label="Delete file">
                          <IconButton
                            size="xs"
                            icon={<FontAwesomeIcon icon={faTrash} />}
                            colorScheme="red"
                            aria-label="Delete file"
                            onClick={() => {
                              if (window.confirm(`Delete "${item.name}"?`))
                                deleteUploadedFile(item);
                            }}
                          />
                        </Tooltip>
                      )}
                    </HStack>
                    </HStack>
                  );
                })}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Rename dialog */}
      <Modal isOpen={showRenameDialog} onClose={() => setShowRenameDialog(false)} size="sm" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">
            Rename {renameTarget?.type === "folder" ? "Folder" : "File"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="New name"
              size="sm"
              onKeyDown={(e) => { if (e.key === "Enter") handleRename(); }}
              autoFocus
            />
            <HStack mt={3} justify="flex-end">
              <Button size="sm" variant="ghost" onClick={() => setShowRenameDialog(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                onClick={handleRename}
                isLoading={isRenaming}
              >
                Rename
              </Button>
            </HStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Share-with-users modal */}
      <ShareRowModal
        isOpen={shareModalOpen}
        onClose={() => {
          setShareModalOpen(false);
          setShareTargetDoc(null);
        }}
        title={`Share "${shareTargetDoc?.title || ""}"`}
        loading={shareModalLoading}
        users={schemaUsers}
        currentUserId={currentUserId}
        alreadySharedIds={alreadySharedIds}
        onShare={handleShare}
        onShareMany={handleShareMany}
        onRemove={handleRemoveShare}
        removingId={shareRemovingId}
        allowAllRoles={true}
        sharedUserRoles={roleByUserId}
        onRoleChange={async (userId, newRole) => {
          const u = schemaUsers.find((x) => x.id === userId) || { id: userId };
          await handleShare(u, newRole);
        }}
      />
    </>
  );
}
